/**
 * DOM Click Tracking Plugin
 *
 * Purpose:
 * Temporarily assigns the nearest parent element's id to buttons and links
 * that do not already have an id, allowing analytics libraries (e.g. Snowplow)
 * to record a meaningful element identifier.
 *
 * The temporary id is removed immediately after the click has been processed.
 */
import { trackLinkClick } from '@snowplow/browser-plugin-link-click-tracking';
import { isNewsletterLink, sourceValue, cssClasses } from '../helpers/click-source';

const WIDGET_CONTAINER_SELECTOR = '#concierge-widget-container';
const WIDGET_ELEMENT_ID = 'widget-conceirge';

const WIDGET_MINIMIZE_ID = 'widget-concierge-minimize';
const WIDGET_CLOSE_ID = 'widget-concierge-close';

const WIDGET_MINIMIZE_ARIA_LABEL = process.env.WIDGET_MINIMIZE_ARIA_LABEL.toLowerCase();
const WIDGET_CLOSE_ARIA_LABEL = process.env.WIDGET_CLOSE_ARIA_LABEL.toLowerCase();

function widgetButtonId(button) {
  const ariaLabel = (button.getAttribute('aria-label') || '').trim().toLowerCase();

  if (ariaLabel.startsWith(WIDGET_MINIMIZE_ARIA_LABEL)) return WIDGET_MINIMIZE_ID;
  if (ariaLabel.startsWith(WIDGET_CLOSE_ARIA_LABEL)) return WIDGET_CLOSE_ID;

  return WIDGET_ELEMENT_ID;
}

export function enableDomClickTracking() {
  if (window.__iirisDomClickEnabled) return;
  window.__iirisDomClickEnabled = true;
  
  document.addEventListener(
    'click',
    (event) => {
      const element = event.target.closest('button, a');

      if (!element) {
        return;
      }

      // Handle all links manually so we can customise
      // elementId only for Concierge links.
      if (element.tagName === 'A') {
        const widgetContainer = element.closest(WIDGET_CONTAINER_SELECTOR);

        if (widgetContainer) {
          element.id = WIDGET_ELEMENT_ID;
        }

        if (isNewsletterLink(element)) {
          trackLinkClick({
            targetUrl: element.href || 'about:invalid',
            elementId: element.id,
            elementClasses: cssClasses(element),
            elementTarget: element.target,
            elementContent: sourceValue(element),
          });
      } else {
        trackLinkClick({
          element,
          trackContent: false,
        });
      }

        if (widgetContainer) {
          setTimeout(() => {
            element.removeAttribute('id');
          }, 0);
        }

        return;
      }

      // Existing Concierge button behaviour
      const widgetContainer = element.closest(WIDGET_CONTAINER_SELECTOR);

      if (!widgetContainer) {
        return;
      }

      if (element.tagName === 'BUTTON' && !element.id) {
        element.id = widgetButtonId(element);

        const ariaLabel = element.getAttribute('aria-label');
        const setLabel =
          element.id !== WIDGET_ELEMENT_ID &&
          ariaLabel &&
          !element.hasAttribute('data-sp-button-label');

        if (setLabel) {
          element.setAttribute('data-sp-button-label', ariaLabel);
        }

        setTimeout(() => {
          element.removeAttribute('id');
          if (setLabel) element.removeAttribute('data-sp-button-label');
        }, 0);
      }
    },
    true
  );
}